import {
  ApplicationRef,
  Component,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ElementRef,
  Injector,
  OnInit,
  Input,
  EventEmitter,
  Output,
  forwardRef,
  Renderer2
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ControlValueAccessor } from '@angular/forms';

import { DateRangePickerTemplateComponent } from './date-range-picker.template.component';
import { DateRangeFormatter } from './date-range-picker.formatter';

@Component({
  host: {
    class: 'axis-daterange-wrapper'
  },
  selector: 'axis-date-range,[axis-date-range]',
  templateUrl: './date-range-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true
    }
  ]
})
export class DateRangePickerComponent implements OnInit, ControlValueAccessor {

  @Input() ariaLabel = 'Please select date range';
  @Input() ariaLabelledby: string;
  @Input() customFormat;
  @Input() disabled;
  @Input() required;
  @Input() placeholder;
  @Input() datepickerMode = 'day';
  @Input() closeOnOutsideClick = true;
  @Output() onSelectRange = new EventEmitter();

  public inputValue = '';
  public rangeComponentTemplateRef: any;
  public modelValue;
  public startDate;
  public endDate;
  public expanded = false;
  public dayModePlaceholder = 'MMM DD, YYYY - MMM DD, YYYY';
  public monthModePlaceholder = 'MMM YYYY - MMM YYYY'
  protected dateFormatter: DateRangeFormatter = new DateRangeFormatter();
  private unbindDocumentEvent: Function;

  // Register ControlValueAccessor methods to update the model and reactive form value.
  private propagateModelChange = (_: any) => { };
  private onTouched: any = (_: any) => { };

  constructor(
    private el: ElementRef,
    private resolver: ComponentFactoryResolver,
    private _cdDetectRef: ChangeDetectorRef,
    private injector: Injector,
    private applicationRef: ApplicationRef,
    private renderer: Renderer2
  ) { }

  ngOnInit() { 
    if(this.datepickerMode === 'month' && !this.customFormat) {
      this.customFormat = 'MMM, YYYY';
    } else if (!this.customFormat) {
      this.customFormat = 'MMM DD, YYYY';
    }
   }

  // Implementing ControlValueAccessor

  writeValue(value: any) {
    if (value && value.startDate && value.endDate && value.startDate <= value.endDate) {
      this.startDate = this.dateFormatter.fixTimeZone(value.startDate);
      this.endDate = this.dateFormatter.fixTimeZone(value.endDate);
      this.inputValue = this.formattedDates(this.startDate, this.endDate);
      this.updateInitialModel(this.startDate, this.endDate);
    } else {
      this.inputValue = '';
      this.updateInitialModel(null, null);
    }
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl) {
    if (this.required || control.errors?.required) {
      // set required true when validator.required set via reactive form. This is for ADA to read required.
      this.required = true;
      if (control.value && control.value.startDate && control.value.endDate) {
        return null;
      } else {
        return {
          axisDateRange: {
            required: true
          }
        };
      }
    }
  }

  formattedDates(startDate, endDate) {
    const inputStartDate = this.dateFormatter.format(startDate, this.customFormat);
    const inputEndDate = this.dateFormatter.format(endDate, this.customFormat);
    return inputStartDate + ' ' + '-' + ' ' + inputEndDate;
  }

  updateModel(startDate, endDate) {
    if (startDate && endDate) {
      if(this.datepickerMode === 'month') {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
      }
      this.modelValue = {
        'startDate': startDate,
        'endDate': endDate
      };
    } else {
      this.modelValue = null;
    }
    this._cdDetectRef.detectChanges();
    this.onSelectRange.emit(this.modelValue); // emit the output event.
    this.propagateModelChange(this.modelValue); // update ngModel and form control value.
    this.onTouched();
  }

  updateInitialModel(startDate, endDate) {
    if (startDate && endDate) {
      this.modelValue = {
        'startDate': startDate,
        'endDate': endDate
      };
    } else {
      this.modelValue = null;
    }
    this._cdDetectRef.detectChanges();
    this.onSelectRange.emit(this.modelValue); // emit the output event.
    this.propagateModelChange(this.modelValue); // update ngModel and form control value.
    this.onTouched();
  }

  createRangeDatePickerTemplate() {
    if (!this.rangeComponentTemplateRef) {

      const componentFactory = this.resolver.resolveComponentFactory(
        DateRangePickerTemplateComponent
      );
      this.rangeComponentTemplateRef = componentFactory.create(this.injector);
      this.applicationRef.attachView(this.rangeComponentTemplateRef.hostView);
      this.el.nativeElement.appendChild(this.rangeComponentTemplateRef.location.nativeElement);
      this.calculatePosition();
      this.rangeComponentTemplateRef.instance.datepickerMode = this.datepickerMode;
      if(this.inputValue === '') {
        this.rangeComponentTemplateRef.instance.customFormat = this.customFormat;
        this.rangeComponentTemplateRef.instance.resetData();
      } else {
        this.rangeComponentTemplateRef.instance.preSelectData(this.customFormat,
          this.inputValue, this.startDate, this.endDate);
      }
      this.rangeComponentTemplateRef.instance.selectionDone.subscribe(data => {
        this.inputValue = data.inputValue;
        this.startDate = data.startDate;
        this.endDate = data.endDate;
        this.updateModel(data.startDate, data.endDate);
      });
      this.rangeComponentTemplateRef.instance.removeTemplate.subscribe(data => {
        if (data === 'remove') {
          this.removeRangeDatePickerTemplate();
          this.el.nativeElement.children[0].children[0].focus();
        }
      });
      if (this.closeOnOutsideClick) {
        this.addDocumentListener();
      }
    }
  }

  openRangeDatePicker(event) {
    this.expanded = true;
    this.createRangeDatePickerTemplate();
  }

  // Listener for document click event
  addDocumentListener() {
    this.unbindDocumentEvent = this.renderer.listen(
      document,
      'click',
      (event: Event) => {
        event.stopPropagation();
        if(event['path'].includes(this.el.nativeElement)) {
          return;
        } else {
          this.removeRangeDatePickerTemplate();
        }
      }
    );
  }

  calculatePosition() {
    const menuElement = this.rangeComponentTemplateRef.location.nativeElement;
    const hostElement = this.el.nativeElement;
    setTimeout(() => {
      if (this.rangeComponentTemplateRef && this.rangeComponentTemplateRef.location) {
        const posObj = hostElement.getBoundingClientRect();
        const menuPosObj = menuElement.getBoundingClientRect();
        this.smartPositioning(posObj, menuPosObj);
      }
    }, 0);
  }

  smartPositioning(posObj: any, menuPosObj: any) {
    const menuHeight = menuPosObj.height;
    if (
      window.innerHeight - (posObj.bottom + menuHeight) > 0 ||
      posObj.top - menuHeight < 0
    ) {
      this.bottomPositioning(posObj, menuPosObj);
    } else {
      this.topPositioning(posObj, menuPosObj, menuHeight);
    }
  }

  topPositioning(posObj: any, menuPosObj: any, menuHeight: number) {
    const top = - menuHeight;
    if (window.innerWidth - (posObj.left + menuPosObj.width) < 0) {
      this.rangeComponentTemplateRef.location.nativeElement.style.left = -menuPosObj.width + posObj.width + 'px';
    }
    this.rangeComponentTemplateRef.location.nativeElement.style.top = top + 'px';
  }
  
  bottomPositioning(posObj: any, menuPosObj: any) {
    if (window.innerWidth - (posObj.left + menuPosObj.width) < 0) {
      this.rangeComponentTemplateRef.location.nativeElement.style.left = -menuPosObj.width + posObj.width + 'px';
    }
  }

  removeRangeDatePickerTemplate() {
    if (this.rangeComponentTemplateRef) {
      this.expanded = false;
      const menuElement = this.rangeComponentTemplateRef.location.nativeElement;
      if (menuElement && menuElement.parentNode) {
        menuElement.parentNode.removeChild(menuElement);
      }
      this.rangeComponentTemplateRef.destroy();
      this.rangeComponentTemplateRef = null;
      if (typeof this.unbindDocumentEvent === 'function') {
        this.unbindDocumentEvent();
       }
    }
  }
}
